import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import LoginPage from "@/pages/LoginPage";
import SandboxInbox from "@/pages/SandboxInbox";
import AppLayout from "@/components/AppLayout";
import Dashboard from "@/pages/Dashboard";
import Timesheet from "@/pages/Timesheet";
import Missions from "@/pages/Missions";
import Simulations from "@/pages/Simulations";
import NewMission from "@/pages/NewMission";
import MissionDetail from "@/pages/MissionDetail";
import AdminUsers from "@/pages/AdminUsers";
import HRArchives from "@/pages/HRArchives";
import ProfitabilityPortfolio from "@/pages/ProfitabilityPortfolio";
import Expenses from "@/pages/Expenses";
import CabinetExpenses from "@/pages/CabinetExpenses";
import OutgoingPOs from "@/pages/OutgoingPOs";
import Suppliers from "@/pages/Suppliers";
import AdminUserExpenses from "@/pages/AdminUserExpenses";
import ExpensesValidation from "@/pages/ExpensesValidation";
import ExpenseSearchPage from "@/pages/ExpenseSearchPage";
import PaymentsDue from "@/pages/PaymentsDue";
import CraLatePage from "@/pages/CraLatePage";
import CraReminderSetup from "@/pages/CraReminderSetup";
import CraReminderManual from "@/pages/CraReminderManual";
import WorkspaceDirectory from "@/pages/WorkspaceDirectory";
import CEOOverview from "@/pages/CEOOverview";
import CeoMailbox from "@/pages/CeoMailbox";
import ManagerRequests from "@/pages/ManagerRequests";
import CRAExport from "@/pages/CRAExport";
import Invoices from "@/pages/Invoices";
import PaidInvoices from "@/pages/PaidInvoices";
import CRAScopeAudit from "@/pages/CRAScopeAudit";
import CRADriveHealthCheck from "@/pages/CRADriveHealthCheck";
import SuppliersDriveHealth from "@/pages/SuppliersDriveHealth";
import CabinetExpensesDriveHealth from "@/pages/CabinetExpensesDriveHealth";
import NdfDriveHealth from "@/pages/NdfDriveHealth";
import DriveOverview from "@/pages/DriveOverview";
import InvoicingAuditHub from "@/pages/InvoicingAuditHub";
import InvoiceRemindersHub from "@/pages/InvoiceRemindersHub";
import ARAging from "@/pages/ARAging";
import Clients from "@/pages/Clients";
import Prospects from "@/pages/Prospects";
import Collaborators from "@/pages/Collaborators";
import CollaboratorDetail from "@/pages/CollaboratorDetail";
import ClientDetail from "@/pages/ClientDetail";
import AllowedDomains from "@/pages/AllowedDomains";
import CustomRoles from "@/pages/CustomRoles";
import RolePermissions from "@/pages/admin/RolePermissions";
import AlertsSettings from "@/pages/settings/AlertsSettings";
import LeavesSettings from "@/pages/settings/LeavesSettings";
import FreelanceSettings from "@/pages/settings/FreelanceSettings";
import MaintenanceSettings from "@/pages/settings/MaintenanceSettings";
import LogsSection from "@/pages/settings/LogsSection";
import NotificationsSettings from "@/pages/settings/NotificationsSettings";
import InvoiceRemindersSettings from "@/pages/settings/InvoiceRemindersSettings";
import BankAccountsSettings from "@/pages/settings/BankAccountsSettings";  // Iter-228w
import InvoiceRemindersAudit from "@/pages/InvoiceRemindersAudit";
import PaymentDeclare from "@/pages/PaymentDeclare";
import PaymentDeclarations from "@/pages/PaymentDeclarations";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Stats from "@/pages/Stats";
import Billing from "@/pages/Billing";
import FreelanceMyProfile from "@/pages/FreelanceMyProfile";
import FreelanceMyInvoices from "@/pages/FreelanceMyInvoices";
import FreelanceAdmin from "@/pages/FreelanceAdmin";
import RecentActivity from "@/pages/RecentActivity";
import AccountConnections from "@/pages/AccountConnections";
import AutoInvoiceDashboard from "@/pages/AutoInvoiceDashboard";
import MissedInvoicesAudit from "@/pages/MissedInvoicesAudit";
import Conges from "@/pages/Conges";
import AdminFreelancers from "@/pages/AdminFreelancers";
import MonthlyStatement from "@/pages/MonthlyStatement";
import MonthlyStatementEmailSettings from "@/pages/MonthlyStatementEmailSettings";
import RealMarginPage from "@/pages/RealMarginPage";
import EmailTracking from "@/pages/EmailTracking";

const FreelanceOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user || !user.is_freelance) return <Navigate to="/dashboard" replace />;
  return children;
};

const ProtectedRoute = ({ children, roles, permissions }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-400 text-sm">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  // Iter-205 — permissions gate. The `/auth/me` response exposes the effective
  // permission set under the `permissions` field (see routers/auth.py:auth_me).
  if (permissions && permissions.length > 0) {
    const eff = user.permissions || [];
    // CEOs bypass since the backend perm catalog already grants them everything.
    if (user.role !== "CEO" && !permissions.some((p) => eff.includes(p))) {
      return <Navigate to="/dashboard" replace />;
    }
  }
  return children;
};

function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Iter-200 — public payment declaration page (no auth, no layout). */}
      <Route path="/payment-declare/:token" element={<PaymentDeclare />} />
      {/* Iter-200c — public RGPD / privacy policy page. */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="timesheet" element={<Timesheet />} />
        <Route path="account/connections" element={<AccountConnections />} />
        <Route path="sandbox/inbox" element={<SandboxInbox />} />
        <Route path="admin/auto-invoice" element={<AutoInvoiceDashboard />} />
        <Route
          path="admin/missed-invoices"
          element={
            <ProtectedRoute roles={["CEO", "ADMIN", "GESTIONNAIRE"]}>
              <MissedInvoicesAudit />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/reminders-audit"
          element={
            <ProtectedRoute roles={["CEO", "ADMIN", "GESTIONNAIRE"]}>
              <InvoiceRemindersAudit />
            </ProtectedRoute>
          }
        />
        {/* Iter-200 — admin queue for client-initiated payment declarations. */}
        <Route
          path="admin/payment-declarations"
          element={
            <ProtectedRoute roles={["CEO", "GESTIONNAIRE"]}>
              <PaymentDeclarations />
            </ProtectedRoute>
          }
        />
        <Route path="conges" element={<Conges />} />
        <Route path="stats" element={<Stats />} />
        <Route
          path="missions"
          element={
            <ProtectedRoute roles={["MANAGER", "CEO", "GESTIONNAIRE"]}>
              <Missions />
            </ProtectedRoute>
          }
        />
        <Route
          path="prospects"
          element={
            <ProtectedRoute roles={["MANAGER", "CEO", "GESTIONNAIRE"]}>
              <Prospects />
            </ProtectedRoute>
          }
        />
        <Route
          path="clients"
          element={
            <ProtectedRoute roles={["MANAGER", "CEO", "GESTIONNAIRE"]}>
              <Clients />
            </ProtectedRoute>
          }
        />
        <Route
          path="clients/:id"
          element={
            <ProtectedRoute roles={["MANAGER", "CEO", "GESTIONNAIRE"]}>
              <ClientDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="collaborators"
          element={
            <ProtectedRoute roles={["MANAGER", "CEO", "GESTIONNAIRE"]}>
              <Collaborators />
            </ProtectedRoute>
          }
        />
        <Route
          path="collaborators/:user_id"
          element={
            <ProtectedRoute roles={["MANAGER", "CEO", "GESTIONNAIRE"]}>
              <CollaboratorDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="missions/new"
          element={
            <ProtectedRoute roles={["MANAGER", "CEO", "GESTIONNAIRE"]}>
              <NewMission />
            </ProtectedRoute>
          }
        />
        <Route
          path="missions/:id"
          element={
            // Iter-210 — collaborators / freelances do not have access to
            // mission details (read-only summary on dashboard is enough).
            <ProtectedRoute roles={["CEO", "ADMIN", "MANAGER", "GESTIONNAIRE"]}>
              <MissionDetail />
            </ProtectedRoute>
          }
        />
        <Route path="simulations" element={<Simulations />} />
        <Route
          path="admin/users"
          element={
            <ProtectedRoute roles={["ADMIN", "CEO"]}>
              <AdminUsers />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/hr-archives"
          element={
            <ProtectedRoute roles={["ADMIN", "CEO"]}>
              <HRArchives />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/cra-late"
          element={
            <ProtectedRoute roles={["ADMIN", "CEO"]}>
              <CraLatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/cra-reminder-setup"
          element={
            <ProtectedRoute roles={["ADMIN", "CEO"]}>
              <CraReminderSetup />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/cra-reminder-manual"
          element={
            <ProtectedRoute roles={["ADMIN", "CEO"]}>
              <CraReminderManual />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/freelancers"
          element={
            <ProtectedRoute roles={["ADMIN", "CEO", "GESTIONNAIRE"]}>
              <AdminFreelancers />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/directory"
          element={
            <ProtectedRoute roles={["ADMIN", "CEO"]}>
              <WorkspaceDirectory />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/custom-roles"
          element={
            <ProtectedRoute roles={["ADMIN", "CEO"]}>
              <CustomRoles />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/role-permissions"
          element={
            <ProtectedRoute roles={["ADMIN", "CEO"]}>
              <RolePermissions />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/domains"
          element={
            <ProtectedRoute roles={["CEO"]}>
              <AllowedDomains />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/settings"
          element={<Navigate to="/settings/alerts" replace />}
        />
        <Route
          path="admin/profitability"
          element={
            <ProtectedRoute roles={["CEO", "ADMIN"]}>
              <ProfitabilityPortfolio />
            </ProtectedRoute>
          }
        />
        <Route path="expenses" element={<Expenses />} />
        <Route path="expenses/:noteId" element={<Expenses />} />
        <Route
          path="admin/users/:userId/expenses"
          element={
            <ProtectedRoute roles={["CEO", "ADMIN", "GESTIONNAIRE"]}>
              <AdminUserExpenses />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/expenses/validation"
          element={
            <ProtectedRoute roles={["CEO", "ADMIN", "GESTIONNAIRE"]}>
              <ExpensesValidation />
            </ProtectedRoute>
          }
        />
        <Route
          path="expenses/search"
          element={
            <ProtectedRoute>
              <ExpenseSearchPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="cabinet-expenses"
          element={
            <ProtectedRoute roles={["CEO", "GESTIONNAIRE"]}>
              <CabinetExpenses />
            </ProtectedRoute>
          }
        />
        <Route
          path="outgoing-pos"
          element={
            <ProtectedRoute permissions={["manage_outgoing_pos"]}>
              <OutgoingPOs />
            </ProtectedRoute>
          }
        />
        <Route
          path="suppliers"
          element={
            <ProtectedRoute permissions={["manage_outgoing_pos"]}>
              <Suppliers />
            </ProtectedRoute>
          }
        />
        <Route
          path="freelance/admin/payments-due"
          element={
            <ProtectedRoute roles={["CEO", "ADMIN", "GESTIONNAIRE"]}>
              <PaymentsDue />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings"
          element={<Navigate to="/settings/alerts" replace />}
        />
        <Route
          path="settings/alerts"
          element={
            <ProtectedRoute roles={["ADMIN", "CEO"]}>
              <AlertsSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/notifications"
          element={
            <ProtectedRoute roles={["ADMIN", "CEO"]}>
              <NotificationsSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/invoice-reminders"
          element={
            <ProtectedRoute roles={["ADMIN", "CEO"]}>
              <InvoiceRemindersSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/bank-accounts"
          element={
            <ProtectedRoute roles={["CEO"]}>
              <BankAccountsSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/leaves"
          element={
            <ProtectedRoute roles={["ADMIN", "CEO", "GESTIONNAIRE"]}>
              <LeavesSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/freelance"
          element={
            <ProtectedRoute roles={["CEO", "GESTIONNAIRE"]}>
              <FreelanceSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/maintenance"
          element={
            <ProtectedRoute roles={["CEO"]}>
              <MaintenanceSettings section="general" />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/maintenance/general"
          element={
            <ProtectedRoute roles={["CEO"]}>
              <MaintenanceSettings section="general" />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/maintenance/invoice-email"
          element={
            <ProtectedRoute roles={["CEO"]}>
              <MaintenanceSettings section="invoice-email" />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/maintenance/outgoing-po-email"
          element={
            <ProtectedRoute roles={["CEO"]}>
              <MaintenanceSettings section="outgoing-po-email" />
            </ProtectedRoute>
          }
        />
        {/* Iter-207v — Monthly Cabinet Statement (Finance pilotage) + dedicated email template settings (CEO). */}
        <Route
          path="admin/monthly-statement"
          element={
            <ProtectedRoute roles={["CEO", "ADMIN", "GESTIONNAIRE"]}>
              <MonthlyStatement />
            </ProtectedRoute>
          }
        />
        {/* Iter-228y — Real company margin (with salaries). CEO / GESTIONNAIRE only. */}
        <Route
          path="admin/real-margin"
          element={
            <ProtectedRoute roles={["CEO", "GESTIONNAIRE"]}>
              <RealMarginPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/maintenance/monthly-statement-email"
          element={
            <ProtectedRoute roles={["CEO"]}>
              <MonthlyStatementEmailSettings />
            </ProtectedRoute>
          }
        />
        {/* Iter-207w — Unified email tracking history (open-rate + per-kind breakdown). */}
        <Route
          path="admin/email-tracking"
          element={
            <ProtectedRoute roles={["CEO", "ADMIN"]}>
              <EmailTracking />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/cra-scope-audit"
          element={
            <ProtectedRoute roles={["CEO", "ADMIN"]}>
              <CRAScopeAudit />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/cra-drive-health-check"
          element={
            <ProtectedRoute roles={["CEO", "ADMIN"]}>
              <CRADriveHealthCheck />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/suppliers-drive-health"
          element={
            <ProtectedRoute roles={["CEO", "ADMIN"]}>
              <SuppliersDriveHealth />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/cabinet-expenses-drive-health"
          element={
            <ProtectedRoute roles={["CEO", "ADMIN"]}>
              <CabinetExpensesDriveHealth />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/ndf-drive-health"
          element={
            <ProtectedRoute roles={["CEO", "ADMIN"]}>
              <NdfDriveHealth />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/drive-overview"
          element={
            <ProtectedRoute roles={["CEO", "ADMIN"]}>
              <DriveOverview />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/invoicing-audit"
          element={
            <ProtectedRoute roles={["CEO", "ADMIN", "GESTIONNAIRE"]}>
              <InvoicingAuditHub />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/invoice-reminders-hub"
          element={
            <ProtectedRoute roles={["CEO", "ADMIN"]}>
              <InvoiceRemindersHub />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/maintenance/cra-export"
          element={
            <ProtectedRoute roles={["CEO"]}>
              <MaintenanceSettings section="cra-export" />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/maintenance/weekly-cra"
          element={
            <ProtectedRoute roles={["CEO"]}>
              <MaintenanceSettings section="weekly-cra" />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/maintenance/monthly-cra"
          element={
            <ProtectedRoute roles={["CEO"]}>
              <MaintenanceSettings section="monthly-cra" />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/maintenance/freelance-flow"
          element={
            <ProtectedRoute roles={["CEO"]}>
              <MaintenanceSettings section="freelance-flow" />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/maintenance/logs"
          element={
            <ProtectedRoute roles={["CEO", "ADMIN"]}>
              <LogsSection />
            </ProtectedRoute>
          }
        />
        <Route
          path="ceo/overview"
          element={
            <ProtectedRoute roles={["CEO"]}>
              <CEOOverview />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/ceo-mailbox"
          element={
            <ProtectedRoute roles={["CEO"]}>
              <CeoMailbox />
            </ProtectedRoute>
          }
        />
        <Route
          path="manager/requests"
          element={
            <ProtectedRoute roles={["MANAGER", "CEO"]}>
              <ManagerRequests />
            </ProtectedRoute>
          }
        />
        <Route
          path="manager/cra-export"
          element={
            <ProtectedRoute roles={["MANAGER", "CEO", "ADMIN", "GESTIONNAIRE"]}>
              <CRAExport />
            </ProtectedRoute>
          }
        />
        <Route
          path="billing"
          element={
            <ProtectedRoute roles={["CEO", "GESTIONNAIRE"]}>
              <Billing />
            </ProtectedRoute>
          }
        />
        <Route
          path="invoices"
          element={
            <ProtectedRoute roles={["CEO", "GESTIONNAIRE"]}>
              <Invoices />
            </ProtectedRoute>
          }
        />
        <Route
          path="invoices/paid"
          element={
            <ProtectedRoute roles={["CEO", "GESTIONNAIRE"]}>
              <PaidInvoices />
            </ProtectedRoute>
          }
        />
        <Route
          path="ar-aging"
          element={
            <ProtectedRoute roles={["CEO", "GESTIONNAIRE"]}>
              <ARAging />
            </ProtectedRoute>
          }
        />
        <Route path="freelance/me" element={<FreelanceOnly><FreelanceMyProfile /></FreelanceOnly>} />
        <Route path="freelance/me/invoices" element={<FreelanceOnly><FreelanceMyInvoices /></FreelanceOnly>} />
        <Route
          path="freelance/admin"
          element={
            <ProtectedRoute roles={["CEO", "ADMIN", "GESTIONNAIRE"]}>
              <FreelanceAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="recent-activity"
          element={
            <ProtectedRoute roles={["CEO", "GESTIONNAIRE"]}>
              <RecentActivity />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App grain-bg">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster
            position="top-right"
            richColors
            closeButton
            duration={6000}
            toastOptions={{
              // Iter-101 → 102:
              //   - closeButton (Toaster prop above) surfaces a ✕ in every toast
              //   - onClick globally dismisses the toast when the user clicks anywhere on it
              //   - duration 6s gives users time to read before auto-dismiss
              onClick: (t) => {
                // `t.id` is the canonical sonner toast id; calling `toast.dismiss(id)` removes it.
                if (t?.id != null) toast.dismiss(t.id);
              },
              classNames: {
                toast: "cursor-pointer",
                closeButton: "!bg-white !border !border-zinc-200 !text-zinc-700 hover:!bg-zinc-50",
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
